import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldWarning, Lock } from "@phosphor-icons/react";

/**
 * Blocking modal shown when the backend flags `must_change_password: true`.
 * User cannot dismiss it — they must change the password to continue.
 * Fixes SEC-006 (default admin password reachable at deploy).
 */
export default function ForcePasswordChange() {
  const { mustChangePassword, clearMustChangePassword, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!mustChangePassword) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error("Le due password non coincidono");
      return;
    }
    if (newPw.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      // SEC: token_version bumped server-side → replace stored token with the fresh one
      if (data?.token) localStorage.setItem("token", data.token);
      toast.success("Password aggiornata. Ora puoi utilizzare l'app.");
      clearMustChangePassword();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4" data-testid="force-password-modal">
      <form onSubmit={submit} className="bg-white border border-neutral-200 shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-neutral-200 bg-amber-50">
          <div className="flex items-center gap-2 mb-2">
            <ShieldWarning size={22} weight="fill" className="text-amber-600" />
            <span className="label-eyebrow text-amber-800">Azione Richiesta</span>
          </div>
          <h2 className="font-display font-bold text-xl tracking-tight">Cambia la password</h2>
          <p className="text-xs text-neutral-600 mt-2">
            Stai utilizzando la password predefinita. Per motivi di sicurezza è obbligatorio impostarne una nuova prima di continuare.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="label-eyebrow block mb-2">Password attuale</span>
            <input
              data-testid="force-pw-current"
              type="password"
              required
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </label>
          <label className="block">
            <span className="label-eyebrow block mb-2">Nuova password (min. 8 caratteri)</span>
            <input
              data-testid="force-pw-new"
              type="password"
              required
              minLength={8}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </label>
          <label className="block">
            <span className="label-eyebrow block mb-2">Conferma nuova password</span>
            <input
              data-testid="force-pw-confirm"
              type="password"
              required
              minLength={8}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </label>
        </div>
        <div className="p-6 pt-0 flex justify-between items-center border-t border-neutral-200 mt-4">
          <button
            type="button"
            data-testid="force-pw-logout"
            onClick={logout}
            className="text-xs text-neutral-500 hover:text-neutral-800 underline"
          >
            Esci dall&apos;account
          </button>
          <button
            data-testid="force-pw-submit"
            type="submit"
            disabled={submitting}
            className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Lock size={14} weight="bold" />
            {submitting ? "Aggiornamento..." : "Aggiorna Password"}
          </button>
        </div>
      </form>
    </div>
  );
}
